/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Table Column helper
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Maxim Fedorov
 * @author Pavel Antonov
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.0.0
 */

import { Fragment } from 'react';

import get from 'lodash/get';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import { getCampaignsInternalPrefilterUrl } from '@/main/pages/PageCampaign/utils/getCampaignsPageUrl';
import { EColorStatus } from '@modules/app/appConstants';
import { appHistoryPush } from '@modules/app/appHistory';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';
import { getPageAccountInfoUrl, isPageAccountInfoUrlExternal } from '@pages/PageAccountInfo';
import {
    getPageAutoReportIdUrl,
    isPageAutoReportIdUrlExternal,
} from '@pages/PageAutoReportId/utils/getPageAutoReportIdUrl';
import { ECampaignsType } from '@pages/PageCampaign';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import { getLabelsImportStatusIcon } from '@pages/PageLabelsImport/components/LabelsImportTable/LabelsImportTableCells';
import { ELabelsImportField } from '@pages/PageLabelsImport/labelsImportConstants';
import { getPageOrderIdUrl, isPageOrderIdExternalUrl } from '@pages/PageOrderId';
import {
    EActionConflictTypes,
    IActionConflicts,
} from '@pages/WizardProductOfferings/wizardProductOfferingsTypes';
import { ExternalRoutePath, openExternalRoute } from '@routes/constants/externalRoutes';
import { CLIENT_ID, ROUTE_CAMPAIGN_ID, RoutePath } from '@routes/constants/routesConstants';
import { formatBalance } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import { icons } from '../../../assets';
import { IPageSubscriberMobileTranslation } from '../../../modules/internationalization/types';
import {
    ELogBusinessEventName,
    ELogBusinessEventType,
    orderEventMapping,
    orderEventType,
} from '../../../modules/logging/loggingConstants';
import declination from '../../../utils/declination';
import { LinkBlock, TextBlockModal, TextBlockStatus } from '../../common';
import { ImgStyled } from '../../common/Block/Img';
import { formatNumberToPhone } from '../../common/PhoneMask/formatNumberToPhone';
import { ETextBlockStatus } from '../../common/TextBlockStatus/textBlockStatusTypes';
import ArrowTooltip from '../../common/Tooltip/Tooltip';
import { tableRowDeleteAction } from '../actions/tableActions';
import { IColumn } from '../components/TableColumns/Columns';
import { CellStyled } from '../components/TableColumns/styled/ColumnsStyled';
import { TableTextStyled } from '../components/TableColumns/styled/TableBlockStyled';
import TableBalanceBlock from '../components/TableColumns/TableBalanceBlock/TableBalanceBlock';
import TableLabelBlock from '../components/TableColumns/TableLabelBlock/TableLabelBlock';
import TableLinkBlock from '../components/TableColumns/TableLinkBlock/TableLinkBlock';
import TableRemoveRowBlock from '../components/TableColumns/TableRemoveRow/TableRemoveRowBlock';
import TableStatusBlock from '../components/TableColumns/TableStatusBlock/TableStatusBlock';
import TableDoubleTextBlock from '../components/TableColumns/TableTextBlock/TableDoubleTextBlock';
import TableTextBlock from '../components/TableColumns/TableTextBlock/TableTextBlock';
import { TableTextConflictBlock } from '../components/TableColumns/TableTextConflictBlock';
import {
    ColumnFieldType,
    ColumnTranslationType,
    EColumnBalance,
    EColumnCell,
    EColumnCellType,
} from '../types/columnTypes';
import { getIsFastActionPage } from '../utils/tableUtils';
import getActionConflictsParsed from './getActionConflictsParsed';
import getCellValue from './getCellValue';
import { getTimeZone } from './getTimeZone';

export const getColumnCell = <Field extends ColumnFieldType, Translation extends ColumnTranslationType>({
    cellType,
    componentsFeature,
    translation,
}: IColumn<Field, Translation>) => {
    // default value to TextBlock if row is empty
    const defaultValue = translation.corporateTable.noValue;
    const noValue = translation.commonTable.noValueFilter;

    const isFastActionPage = getIsFastActionPage();

    switch (cellType) {
        case EColumnCell.linkAccount:
            return (data: any) => {
                const url = getPageAccountInfoUrl(componentsFeature, data.row.original.id);
                const logBusinessEventPayload: ILogBusinessEventPayload = {
                    eventName: ELogBusinessEventName.USER_ACCOUNT,
                    type: ELogBusinessEventType.common,
                };

                if (isFastActionPage) {
                    return (
                        <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                            {data.row.original.number}
                        </TableTextBlock>
                    )
                }

                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAccountInfoUrlExternal(componentsFeature)}
                        logBusinessEventPayload={logBusinessEventPayload}
                        url={url}
                    >
                        {data.row.original.number}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkAccountExpenses:
            return (data: any) => {
                const url = getPageAccountInfoUrl(componentsFeature, data.row.original.bisAccountId);
                const logBusinessEventPayload: ILogBusinessEventPayload = {
                    eventName: ELogBusinessEventName.USER_ACCOUNT,
                    type: ELogBusinessEventType.common,
                };
                return data.row.original.expandedColumn ? (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {data.row.original.expandedColumn}
                    </TableTextBlock>
                ) : (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAccountInfoUrlExternal(componentsFeature)}
                        logBusinessEventPayload={logBusinessEventPayload}
                        url={url}
                    >
                        {data.row.original.account}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkAction:
            return (data: any) => {
                const historyId = get(data, 'row.original.historyId', '');
                const orderTypeId: string = get(data, 'row.original.action.id', '');
                const eventName =
                    // @ts-ignore
                    orderEventMapping[orderEventType.user][orderTypeId] ||
                    ELogBusinessEventName.USER_SERVICES_ORDER_INFO;

                const url = getPageOrderIdUrl(componentsFeature, historyId);
                const logBusinessEventPayload: ILogBusinessEventPayload = {
                    eventName: eventName,
                    type: ELogBusinessEventType.common,
                };
                const value = get(data, 'row.original.action.def', '');
                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageOrderIdExternalUrl(componentsFeature)}
                        logBusinessEventPayload={logBusinessEventPayload}
                        url={url}
                    >
                        {value}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkMobile:
        case EColumnCell.linkMobileCampaign:
            return (data: any) => {
                let id = data.row.original.id;
                if (cellType === EColumnCell.linkMobileCampaign) {
                    id = data.row.original.subsId;
                }
                const url = RoutePath.subscriberId.replace(CLIENT_ID, id);
                const phoneNumber = formatNumberToPhone(data.row.original.msisdn);

                const logBusinessEventPayload: ILogBusinessEventPayload = {
                    eventName: ELogBusinessEventName.USER_SUBSCRIBER_CARD,
                    type: ELogBusinessEventType.common,
                };
                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        logBusinessEventPayload={logBusinessEventPayload}
                        url={url}
                    >
                        {phoneNumber}
                    </TableLinkBlock>
                );
            };

        case EColumnCell.linkAccountNumber:
            return (row: any) => {
                const cellValue = get(row, 'cell.value', { number: '', id: '' });
                const url = getPageAccountInfoUrl(componentsFeature, cellValue.id);
                const logBusinessEventPayload: ILogBusinessEventPayload = {
                    eventName: ELogBusinessEventName.USER_ACCOUNT,
                    type: ELogBusinessEventType.common,
                };
                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAccountInfoUrlExternal(componentsFeature)}
                        logBusinessEventPayload={logBusinessEventPayload}
                        url={url}
                    >
                        {cellValue.number}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkCampaignAccount:
            return (props: any) => {
                const number = props.row.original.number;
                const id = props.row.original.clientId;
                const url = getPageAccountInfoUrl(componentsFeature, id);

                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAccountInfoUrlExternal(componentsFeature)}
                        url={url}
                    >
                        {number}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkCampaignAccountNumber:
            return (props: any) => {
                const number = props.row.original.accountNumber;
                const id = props.row.original.accountId;
                const url = getPageAccountInfoUrl(componentsFeature, id);

                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAccountInfoUrlExternal(componentsFeature)}
                        url={url}
                    >
                        {number}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkCampaignNumber:
            return (props: any) => {
                const cellValue = props.cell.value;
                const id = props.row.original.id;
                const msisdn = props.row.original.msisdn;
                const type: ECampaignsType = msisdn ? ECampaignsType.subscriber : ECampaignsType.account;
                const url = getCampaignsInternalPrefilterUrl(type, msisdn || id);

                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        url={url}
                    >
                        {cellValue}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkCampaignId:
            return (props: any) => {
                const cellValue = props.cell.value;
                const id = props.row.original.id;

                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        url={RoutePath.campaignInfo.replace(ROUTE_CAMPAIGN_ID, id)}
                    >
                        {cellValue}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkSettingsAutoReports:
            return (props: any) => {
                const cellValue = props.cell.value.def ?? '';
                const id = props.row.original.cppoId;

                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAutoReportIdUrlExternal(componentsFeature)}
                        url={getPageAutoReportIdUrl(componentsFeature, id)}
                    >
                        {cellValue}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.linkUnpaidBillsNumber:
            return (props: any) => {
                const cellValue = props.cell.value;
                const id = props.row.original.id;
                const url = RoutePath.billsUnpaid + '/account/' + id;
                const openBillsUnpaid = () => appHistoryPush(url);
                return (
                    <TableLinkBlock defaultValue={defaultValue} url={url} onClick={openBillsUnpaid}>
                        {cellValue}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.count:
            return (row: any) => (
                <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                    {row.cell.value}
                </TableTextBlock>
            );
        case EColumnCell.notEmpty:
            return (row: any) => (
                <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                    {row.cell.value}
                </TableTextBlock>
            );
        case EColumnCell.amount:
            return (row: any) => {
                const value = get(row, 'cell.value', 0);
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {formatBalance(value) + addSpecialSignToFilter(EColumnCell.balance, translation, value)}
                    </TableTextBlock>
                );
            };
        case EColumnCell.actionConflicts:
            return (row: any) => {
                const actionConflicts: IActionConflicts = get(row, 'cell.value', []);
                const actionConflictsParsed = getActionConflictsParsed<ETextBlockStatus>(actionConflicts, {
                    [EActionConflictTypes.ERROR]: translation.corporateTable.actionConflictsTitle.error,
                    [EActionConflictTypes.WARNING]: translation.corporateTable.actionConflictsTitle.warning,
                    tCustomErrors: translation.corporateTable.actionConflictsTitle.numberCodeCustomErrors,
                });

                return (
                    <TextBlockModal
                        disabled={actionConflictsParsed.status === ETextBlockStatus.OK}
                        text={actionConflictsParsed.text}
                    >
                        <TextBlockStatus status={actionConflictsParsed.status}>
                            <LinkBlock disabled={actionConflictsParsed.status === ETextBlockStatus.OK}>
                                {translation.corporateTable.availability[actionConflictsParsed.status]}
                            </LinkBlock>
                        </TextBlockStatus>
                    </TextBlockModal>
                );
            };
        case EColumnCell.balance:
        case EColumnCell.balanceScore:
            return (row: any) => {
                const cellValue = get(row, 'cell.value', { value: 0, date: 0 });
                const balance =
                    cellValue.value || (cellValue.value === 0 && cellValue.date) ? formatBalance(cellValue.value) : '';
                const valueTime = cellValue.date
                    ? `${moment(cellValue.date).format('DD.MM.YY HH:mm')} ${getTimeZone(cellValue.date)}`
                    : '';
                const balanceType = getBalanceType(cellValue.value);
                return (
                    <TableBalanceBlock
                        balanceType={balanceType}
                        currency={addSpecialSignToFilter(cellType, translation, cellValue.value)}
                        date={valueTime}
                        defaultValue={defaultValue}
                    >
                        {balance}
                    </TableBalanceBlock>
                );
            };
        case EColumnCell.cost:
            return (row: any) => {
                const cost: number = get(row, 'cell.value', 0);
                return (
                    <TableBalanceBlock
                        balanceType={EColumnBalance.zero}
                        currency={addSpecialSignToFilter(EColumnCell.cost, translation, cost)}
                        defaultValue={defaultValue}
                    >
                        {formatBalance(cost)}
                    </TableBalanceBlock>
                );
            };
        case EColumnCell.score:
            return (row: any) => {
                const score: number = get(row, 'cell.value', 0);
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {formatBalance(score)}&thinsp;{declination(score, translation.scores)}
                    </TableTextBlock>
                );
            };
        case EColumnCell.dateCell:
            return (row: any) => {
                const value = get(row, 'cell.value', '');
                const date = value ? moment(value).format(EFormatData.FullDateWithTime) : '';
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {date}
                    </TableTextBlock>
                );
            };
        case EColumnCell.closeDate:
        case EColumnCell.createDate:
        case EColumnCell.updateDate:
            return (row: any) => {
                const value = get(row, 'cell.value', '');
                const date = value ? moment(value).format(EFormatData.FullData) : '';
                const time = value ? `${moment(value).format(EFormatData.OnlyHourMinutes)} ${getTimeZone(value)}` : '';
                return (
                    <TableDoubleTextBlock defaultValue={defaultValue} secondValue={time}>
                        {date}
                    </TableDoubleTextBlock>
                );
            };
        case EColumnCell.removeAutoReport:
            return (row: any) => {
                const id = row.row.original.cppoId ?? 0;
                return <TableRemoveRowBlock rowId={id} />;
            };
        case EColumnCell.schedule:
            return (props: any) => {
                const name = props.cell.value.def ?? '';
                const day = props.row.original.dayOfPeriodName ?? '';
                return (
                    <TableDoubleTextBlock defaultValue={defaultValue} secondValue={day}>
                        {name}
                    </TableDoubleTextBlock>
                );
            };
        case EColumnCell.statusLabelsImport:
            return (row: any) => {
                const cellValue = get(row, 'cell.value', {});
                const { icon, colorState } = getLabelsImportStatusIcon(cellValue.id);
                return (
                    <CellStyled>
                        <TableStatusBlock
                            colorState={colorState}
                            defaultValue={defaultValue}
                            icon={icon}
                            status={cellValue.def}
                        />
                    </CellStyled>
                );
            };
        case EColumnCell.timeCell:
            return (row: any) => {
                const value = row.cell.value ?? '';
                const time = value ? moment(value).format(EFormatData.OnlyHourMinutes) : '';
                const timeZone = value ? getTimeZone(value) : '';
                return (
                    <TableDoubleTextBlock defaultValue={defaultValue} secondValue={timeZone}>
                        {time}
                    </TableDoubleTextBlock>
                );
            };
        case EColumnCell.labels:
            return (row: any) => {
                const cellValue = get(row, 'cell.value', []);
                return (
                    <TableLabelBlock
                        defaultValue={defaultValue}
                        translation={translation.table as IPageSubscriberMobileTranslation['table']}
                        value={cellValue}
                    />
                );
            };
        case EColumnCell.linkFixedAccount:
            return (data: any) => {
                const url = ExternalRoutePath.subscriberFixed + '/' + data.row.original.id;
                const openAccount = () => {
                    openExternalRoute(url);
                };
                return (
                    <TableLinkBlock defaultValue={defaultValue} url={url} onClick={openAccount}>
                        {data.row.original.msisdn}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.billsPeriod:
            return (data: any) => {
                const valueStart = data.row.original?.startDate ?? '';
                const start = valueStart ? moment(valueStart).format(EFormatData.FullData) : '';
                const valueEnd = data.row.original?.endDate ?? '';
                const end = valueEnd ? moment(valueEnd).format(EFormatData.FullData) : '';
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {`${start} - ${end}`}
                    </TableTextBlock>
                );
            };
        case EColumnCell.phoneText:
            return (data: any) => {
                const serviceUserLogin: string = get(data, 'row.original.serviceUser', null);
                const isServiceUser: boolean = serviceUserLogin !== null;
                const login: string = isServiceUser ? serviceUserLogin : formatNumberToPhone(data.cell.value);
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {isServiceUser
                            ? stringFormatParams(translation.corporateTable.serviceAccess.loginPrefix, { login })
                            : login}
                    </TableTextBlock>
                );
            };
        case EColumnCell.billsAccount:
            return (data: any) => {
                const url = getPageAccountInfoUrl(componentsFeature, data.row.original.clientId);
                const logBusinessEventPayload: ILogBusinessEventPayload = {
                    eventName: ELogBusinessEventName.USER_ACCOUNT,
                    type: ELogBusinessEventType.common,
                };
                const openAccount = () => {
                    openExternalRoute(url);
                };
                if (isFastActionPage) {
                    return (
                        <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                            {data.row.original.customer}
                        </TableTextBlock>
                    )
                }
                return (
                    <TableLinkBlock
                        defaultValue={defaultValue}
                        isExternal={isPageAccountInfoUrlExternal(componentsFeature)}
                        logBusinessEventPayload={logBusinessEventPayload}
                        url={url}
                        onClick={openAccount}
                    >
                        {data.row.original.customer}
                    </TableLinkBlock>
                );
            };
        case EColumnCell.billsPayStatus:
            return (data: any) => {
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {data.row.original?.payStatus?.def}
                    </TableTextBlock>
                );
            };
        case EColumnCell.labelsImportConflict:
            return (data: any) => {
                const value = data.cell.value?.name;
                const conflict =
                    data.column.id === ELabelsImportField.MSISDN || value ? data.cell.value?.conflict : undefined;
                const colorState = conflict ? EColorStatus.ERROR : undefined;
                return (
                    <TableTextConflictBlock colorState={colorState} conflict={conflict} defaultValue="" value={value} />
                );
            };
        case EColumnCell.importPersonalDataStatus:
            return (row: any) => {
                const value = row.cell.value;
                const conflicts = value?.conflicts || [];
                const status = conflicts.length > 0 ? ETextBlockStatus.ERROR : ETextBlockStatus.OK;

                let text = '';
                if (conflicts.length > 0) {
                    text += `<h3>${value?.conflictTitle}</h3>`;
                    conflicts.forEach((conflict: string) => {
                        text += `<p>${conflict}</p>`;
                    });
                }

                return (
                    <TextBlockModal disabled={status === ETextBlockStatus.OK} text={text}>
                        <TextBlockStatus status={status}>
                            <LinkBlock disabled={status === ETextBlockStatus.OK}>{value?.title}</LinkBlock>
                        </TextBlockStatus>
                    </TextBlockModal>
                );
            };

        case EColumnCell.importSubscribersLimitsStatus:
            return (row: any) => {
                const value = row.cell.value;
                const isError = value?.conflictTitle?.length > 0;
                const statusValue = isError ? ETextBlockStatus.ERROR : ETextBlockStatus.OK;
                const statusText = value?.title;

                return (

                        <ArrowTooltip
                            placement='top'
                            title={value?.conflictTitle}
                        >
                            <div>
                                <TextBlockStatus status={statusValue}>
                                    <LinkBlock
                                        disabled={!isError}
                                    >
                                        {statusText}
                                    </LinkBlock>
                                </TextBlockStatus>
                            </div>
                        </ArrowTooltip>

                );
            }
        case EColumnCell.persDataStatusName:
            return (row: any) => {
                const description = getCellValue(row.cell.row.original?.persDataStatusDescription);
                const hintCode = getCellValue(row.cell.row.original?.persDataHintCode) as string;
                const hint =
                    description ||
                    (hintCode && (translation.status.codes[hintCode] || translation.status.defaultMessage));

                return (
                    <Fragment>
                        {hint && (
                            <div className="personal-data-hint" style={{ display: 'none' }}>
                                {hint}
                            </div>
                        )}
                        <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                            {row.cell.value ?? ''}
                        </TableTextBlock>
                    </Fragment>
                );
            };
        case EColumnCell.rowDeleteAction:
            return (row: any) => {
                const dispatch = useDispatch();

                return (
                    <div>
                        <ImgStyled
                            data-name="table-cell-row-delete-action"
                            src={icons.removeSmallOpacityIcon}
                            width="24px"
                            onClick={() => dispatch(tableRowDeleteAction({ index: row.row.index }))}
                        />
                    </div>
                );
            };
        case EColumnCell.textDictionary:
            return (row: any) => {
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {row.cell.value.def ?? ''}
                    </TableTextBlock>
                );
            };
        case EColumnCell.textHtml:
            return (row: any) => {
                return <TableTextStyled dangerouslySetInnerHTML={{ __html: row.cell.value || defaultValue }} />;
            };
        case EColumnCell.text:
        default:
            return (row: any) => {
                return (
                    <TableTextBlock defaultValue={defaultValue} noValue={noValue}>
                        {row.cell.value}
                    </TableTextBlock>
                );
            };
    }
};

export const getColumnCellClass = <Field extends ColumnFieldType, Translation extends ColumnTranslationType>({
    cellType,
}: IColumn<Field, Translation>) => {
    switch (cellType) {
        case EColumnCell.amount:
        case EColumnCell.count:
            return 'table__tooltip-text right';
        case EColumnCell.balance:
        case EColumnCell.balanceScore:
        case EColumnCell.cost:
            return 'table__balance-block right';

        case EColumnCell.closeDate:
        case EColumnCell.createDate:
        case EColumnCell.updateDate:
        case EColumnCell.timeCell:
            return 'table__date-block table__double-text';

        case EColumnCell.labelsImportConflict:
        case EColumnCell.schedule:
            return 'table__double-text';
        case EColumnCell.labels:
            return 'table__label-block';
        case EColumnCell.linkCampaignNumber:
            return 'table__link-block link__campaign-count right';
        case EColumnCell.linkUnpaidBillsNumber:
            return 'table__link-block link__unpaid-bills right';

        case EColumnCell.linkFixedAccount:
        case EColumnCell.linkAccount:
        case EColumnCell.linkAction:
        case EColumnCell.linkMobile:
        case EColumnCell.linkMobileCampaign:
        case EColumnCell.linkAccountNumber:
        case EColumnCell.linkCampaignAccount:
        case EColumnCell.linkCampaignAccountNumber:
        case EColumnCell.linkCampaignId:
            return 'table__link-block';

        case EColumnCell.persDataStatusName:
            return 'table__personalStatus-text table__tooltip-text';

        case EColumnCell.billsPayStatus:
        case EColumnCell.billsPeriod:
        case EColumnCell.dateCell:
        case EColumnCell.notEmpty:
        case EColumnCell.phoneText:
        case EColumnCell.text:
        default:
            return 'table__tooltip-text';
    }
};
export const getColumnCellRowCount = <Field extends ColumnFieldType, Translation extends ColumnTranslationType>({
    cellType,
}: IColumn<Field, Translation>) => {
    switch (cellType) {
        case EColumnCell.balance:
        case EColumnCell.balanceScore:
        case EColumnCell.closeDate:
        case EColumnCell.createDate:
        case EColumnCell.labelsImportConflict:
        case EColumnCell.schedule:
        case EColumnCell.timeCell:
        case EColumnCell.updateDate:
            return EColumnCellType.double;
        default:
            return EColumnCellType.default;
    }
};
export const addSpecialSignToFilter = (
    cellType: EColumnCell,
    translation: ColumnTranslationType,
    value: number,
): string => {
    switch (cellType) {
        case EColumnCell.balance:
        case EColumnCell.cost:
            return ` ${translation.currency}`;
        case EColumnCell.balanceScore:
            return ` ${declination(value, translation.scores)}`;
        default:
            return '';
    }
};

export const getBalanceType = (value: number): EColumnBalance => {
    if (value > 0) {
        return EColumnBalance.positive;
    } else if (value < 0) {
        return EColumnBalance.negative;
    } else {
        return EColumnBalance.zero;
    }
};
